import { useEffect, useState } from 'react';
import { Plus, Search, ArrowLeft, Edit2, UserCheck, UserX, Eye, Mail } from 'lucide-react';
import { AlertModal } from '../../components/AlertModal';
import { ConfirmModal } from '../../components/ConfirmModal';
import { PromptModal, PromptField } from '../../components/PromptModal';

const API = import.meta.env.VITE_API_URL;

interface Affiliate {
  id: string;
  referral_code: string;
  display_name: string;
  legal_name: string | null;
  email: string;
  status: 'onboarding' | 'enrolled' | 'suspended' | 'terminated';
  effective_start_date: string;
  payment_method_preference: string;
  tier_enabled: boolean;
  second_level_enabled: boolean;
  default_commission_pct: number;
  linked_location_id: string | null;
  linked_upline_affiliate_id: string | null;
  terminated_at: string | null;
  running_balance?: number;
}

type Mode = 'list' | 'detail' | 'create' | 'edit';

const statusBadge = (status: string) => {
  const colors: Record<string, string> = {
    onboarding: 'bg-yellow-100 text-yellow-800',
    enrolled: 'bg-green-100 text-green-800',
    suspended: 'bg-orange-100 text-orange-800',
    terminated: 'bg-gray-200 text-gray-700',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

export default function AffiliatesList() {
  const [mode, setMode] = useState<Mode>('list');
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const fetchList = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (statusFilter) params.set('status', statusFilter);
    const res = await fetch(`${API}/admin/affiliates?${params}`, { credentials: 'include' });
    const json = (await res.json()) as { success: boolean; data: Affiliate[] };
    if (json.success) setAffiliates(json.data || []);
    setLoading(false);
  };

  useEffect(() => {
    if (mode === 'list') fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, statusFilter]);

  if (mode === 'detail' && selectedId) {
    return (
      <AffiliateDetail
        affiliateId={selectedId}
        onBack={() => {
          setSelectedId(null);
          setMode('list');
        }}
        onEdit={() => setMode('edit')}
      />
    );
  }

  if (mode === 'create' || (mode === 'edit' && selectedId)) {
    return (
      <AffiliateForm
        affiliateId={mode === 'edit' ? selectedId : null}
        onCancel={() => setMode(selectedId ? 'detail' : 'list')}
        onSaved={(id) => {
          setSelectedId(id);
          setMode('detail');
        }}
      />
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-gray-900">Affiliates</h2>
        <button
          onClick={() => {
            setSelectedId(null);
            setMode('create');
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" /> New Affiliate
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-4 flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, or referral code..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchList()}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          <option value="">All statuses</option>
          <option value="onboarding">Onboarding</option>
          <option value="enrolled">Enrolled</option>
          <option value="suspended">Suspended</option>
          <option value="terminated">Terminated</option>
        </select>
        <button
          onClick={fetchList}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
        >
          Search
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : affiliates.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No affiliates found.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-3 font-medium text-gray-700">Name</th>
                <th className="text-left p-3 font-medium text-gray-700">Code</th>
                <th className="text-left p-3 font-medium text-gray-700">Email</th>
                <th className="text-left p-3 font-medium text-gray-700">Status</th>
                <th className="text-left p-3 font-medium text-gray-700">Method</th>
                <th className="text-left p-3 font-medium text-gray-700">Start</th>
                <th className="text-right p-3 font-medium text-gray-700">Balance</th>
              </tr>
            </thead>
            <tbody>
              {affiliates.map((a) => (
                <tr
                  key={a.id}
                  onClick={() => {
                    setSelectedId(a.id);
                    setMode('detail');
                  }}
                  className="border-b hover:bg-gray-50 cursor-pointer"
                >
                  <td className="p-3">{a.display_name}</td>
                  <td className="p-3 font-mono text-xs">{a.referral_code}</td>
                  <td className="p-3 text-gray-600">{a.email}</td>
                  <td className="p-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${statusBadge(a.status)}`}>
                      {a.status}
                    </span>
                  </td>
                  <td className="p-3 text-xs text-gray-600">{a.payment_method_preference}</td>
                  <td className="p-3 text-xs">{a.effective_start_date}</td>
                  <td className={`p-3 text-right font-mono ${a.running_balance && a.running_balance < 0 ? 'text-red-600 font-bold' : ''}`}>
                    ${(a.running_balance || 0).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Affiliate Detail (with sub-tabs)
// ============================================================================

interface AffiliateDetailProps {
  affiliateId: string;
  onBack: () => void;
  onEdit: () => void;
}

function AffiliateDetail({ affiliateId, onBack, onEdit }: AffiliateDetailProps) {
  const [detail, setDetail] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState<'profile' | 'tiers' | 'referrals' | 'periods' | 'payments' | 'documents'>('profile');
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [confirmState, setConfirmState] = useState<
    { title: string; message: string; danger?: boolean; onConfirm: () => void } | null
  >(null);
  const [promptState, setPromptState] = useState<
    { title: string; message?: string; fields: PromptField[]; danger?: boolean; onSubmit: (v: Record<string, string>) => void } | null
  >(null);

  const fetchDetail = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/affiliates/${affiliateId}`, { credentials: 'include' });
      const json = (await res.json()) as { success: boolean; data: any; error?: string };
      if (json.success) setDetail(json.data);
      else setActionError(json.error || 'Failed to load affiliate');
    } catch (e) {
      setActionError(`Could not reach the server: ${String(e)}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [affiliateId]);

  const enroll = async () => {
    setActionError(null);
    try {
      const res = await fetch(`${API}/admin/affiliates/${affiliateId}/transition-enrolled`, {
        method: 'POST',
        credentials: 'include',
      });
      const json = (await res.json()) as { success: boolean; error?: string };
      if (!json.success) setActionError(json.error || 'Failed to enroll');
    } catch (e) {
      setActionError(`Could not reach the server: ${String(e)}`);
    }
    fetchDetail();
  };

  const suspend = () => {
    setConfirmState({
      title: 'Suspend Affiliate',
      message: 'Suspend this affiliate? They will continue to accrue commissions but will not be paid until re-enrolled.',
      onConfirm: async () => {
        setConfirmState(null);
        try {
          const res = await fetch(`${API}/admin/affiliates/${affiliateId}/suspend`, {
            method: 'POST',
            credentials: 'include',
          });
          const json = (await res.json()) as { success: boolean; error?: string };
          if (!json.success) setActionError(json.error || 'Failed to suspend');
        } catch (e) {
          setActionError(`Could not reach the server: ${String(e)}`);
        }
        fetchDetail();
      },
    });
  };

  const terminate = () => {
    setPromptState({
      title: 'Terminate Affiliate',
      message: 'Termination is permanent — no further commissions accrue. Choose how to handle any outstanding balance.',
      danger: true,
      fields: [
        {
          name: 'disposition',
          label: 'Balance disposition',
          type: 'select',
          required: true,
          options: [
            { value: 'pay_out', label: 'Pay out — process remaining balance normally' },
            { value: 'write_off', label: 'Write off — post a debit that zeroes the balance' },
            { value: 'leave_as_is', label: 'Leave as-is — balance remains, decide later' },
          ],
        },
        { name: 'reason', label: 'Reason for termination', type: 'textarea', required: true },
      ],
      onSubmit: async (v) => {
        setPromptState(null);
        try {
          const res = await fetch(`${API}/admin/affiliates/${affiliateId}/terminate`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ balance_disposition: v.disposition, disposition_reason: v.reason }),
          });
          const json = (await res.json()) as { success: boolean; data?: any; error?: string };
          if (!json.success) setActionError(json.error || 'Failed to terminate');
          else setActionSuccess('Affiliate terminated.');
        } catch (e) {
          setActionError(`Could not reach the server: ${String(e)}`);
        }
        fetchDetail();
      },
    });
  };

  const sendInvite = () => {
    setConfirmState({
      title: 'Send Portal Invite',
      message: 'Send portal invite email? Creates an affiliate portal login if none exists and emails a setup link.',
      onConfirm: async () => {
        setConfirmState(null);
        setActionError(null);
        try {
          const res = await fetch(`${API}/admin/affiliates/${affiliateId}/invite`, {
            method: 'POST',
            credentials: 'include',
          });
          const json = (await res.json()) as { success: boolean; message?: string; error?: string };
          if (!json.success) setActionError(json.error || 'Failed to send invite');
          else setActionSuccess(json.message || 'Invite sent.');
        } catch (e) {
          setActionError(`Could not reach the server: ${String(e)}`);
        }
      },
    });
  };

  const viewAsAffiliate = async () => {
    setActionError(null);
    try {
      const res = await fetch(`${API}/admin/affiliates/${affiliateId}/impersonate`, {
        method: 'POST',
        credentials: 'include',
      });
      const json = (await res.json()) as { success: boolean; data?: any; error?: string };
      if (!json.success) {
        setActionError(json.error || 'Failed to start impersonation');
        return;
      }
      window.open(json.data.impersonation_url, '_blank', 'noopener');
    } catch (e) {
      setActionError(`Could not reach the server: ${String(e)}`);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;
  if (!detail) {
    return (
      <div>
        <div className="p-8 text-center text-gray-500">Affiliate not found.</div>
        <AlertModal isOpen={!!actionError} title="Error" message={actionError || ''} onClose={() => setActionError(null)} />
      </div>
    );
  }

  const aff = detail.affiliate;

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-2 text-blue-600 hover:underline mb-4 text-sm">
        <ArrowLeft className="w-4 h-4" /> Back to list
      </button>

      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6 mb-4">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{aff.display_name}</h2>
            <div className="flex items-center gap-3 mt-1 text-sm text-gray-600">
              <span className="font-mono">{aff.referral_code}</span>
              <span>•</span>
              <span>{aff.email}</span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusBadge(aff.status)}`}>{aff.status}</span>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={onEdit} className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm">
              <Edit2 className="w-4 h-4" /> Edit
            </button>
            <button
              onClick={viewAsAffiliate}
              title="Open the affiliate portal as this affiliate (admin impersonation, audited)"
              className="flex items-center gap-1 px-3 py-1.5 bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 text-sm"
            >
              <Eye className="w-4 h-4" /> View as
            </button>
            <button
              onClick={sendInvite}
              title="Send portal invite email (creates login if needed)"
              className="flex items-center gap-1 px-3 py-1.5 bg-cyan-100 text-cyan-800 rounded-lg hover:bg-cyan-200 text-sm"
            >
              <Mail className="w-4 h-4" /> Send Invite
            </button>
            {aff.status === 'onboarding' && (
              <button onClick={enroll} className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm">
                <UserCheck className="w-4 h-4" /> Enroll
              </button>
            )}
            {aff.status === 'enrolled' && (
              <button onClick={suspend} className="px-3 py-1.5 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 text-sm">
                Suspend
              </button>
            )}
            {aff.status === 'suspended' && (
              <button onClick={enroll} className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 text-sm">
                Re-enroll
              </button>
            )}
            {aff.status !== 'terminated' && (
              <button onClick={terminate} className="flex items-center gap-1 px-3 py-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm">
                <UserX className="w-4 h-4" /> Terminate
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t">
          <div>
            <div className="text-xs text-gray-500">Running Balance</div>
            <div className={`text-xl font-mono font-bold ${detail.running_balance < 0 ? 'text-red-600' : 'text-gray-900'}`}>
              ${detail.running_balance.toFixed(2)}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Effective Start</div>
            <div className="text-sm font-medium">{aff.effective_start_date}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Payment Method</div>
            <div className="text-sm font-medium">{aff.payment_method_preference}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Tier / Second-Level</div>
            <div className="text-sm font-medium">
              {aff.tier_enabled ? 'Tiered' : `Flat ${aff.default_commission_pct}%`}
              {aff.second_level_enabled && ' + 2nd level'}
            </div>
          </div>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b">
          <nav className="flex flex-wrap gap-x-6 px-4">
            {(['profile', 'tiers', 'referrals', 'periods', 'payments', 'documents'] as const).map((t) => (
              <button
                key={t}
                onClick={() => setSubTab(t)}
                className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                  subTab === t ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </nav>
        </div>
        <div className="p-4">
          {subTab === 'profile' && <ProfileTab aff={aff} affiliateId={affiliateId} />}
          {subTab === 'tiers' && <TiersTab affiliateId={affiliateId} affiliate={aff} />}
          {subTab === 'referrals' && <ReferralsTab affiliateId={affiliateId} referrals={detail.referrals} onChange={fetchDetail} />}
          {subTab === 'periods' && <PeriodsTab periods={detail.periods} />}
          {subTab === 'payments' && <PaymentsTab payments={detail.payments} />}
          {subTab === 'documents' && <DocumentsTab documents={detail.documents} />}
        </div>
      </div>

      <AlertModal isOpen={!!actionError} title="Error" message={actionError || ''} onClose={() => setActionError(null)} />
      <AlertModal isOpen={!!actionSuccess} title="Success" variant="success" message={actionSuccess || ''} onClose={() => setActionSuccess(null)} />
      <ConfirmModal
        isOpen={!!confirmState}
        title={confirmState?.title || ''}
        message={confirmState?.message || ''}
        danger={confirmState?.danger}
        onConfirm={() => confirmState?.onConfirm()}
        onCancel={() => setConfirmState(null)}
      />
      <PromptModal
        isOpen={!!promptState}
        title={promptState?.title || ''}
        message={promptState?.message}
        fields={promptState?.fields || []}
        danger={promptState?.danger}
        submitLabel={promptState?.danger ? 'Confirm' : 'Submit'}
        onSubmit={(v) => promptState?.onSubmit(v)}
        onCancel={() => setPromptState(null)}
      />
    </div>
  );
}

function ProfileTab({ aff, affiliateId }: { aff: any; affiliateId: string }) {
  const [pii, setPii] = useState<any>(null);
  const [revealing, setRevealing] = useState(false);
  const [revealError, setRevealError] = useState<string | null>(null);
  const [reasonPromptOpen, setReasonPromptOpen] = useState(false);

  // Auto-hide reveal after 30s
  useEffect(() => {
    if (!pii) return;
    const t = setTimeout(() => setPii(null), 30000);
    return () => clearTimeout(t);
  }, [pii]);

  const reveal = () => {
    if (pii) {
      setPii(null);
      return;
    }
    setReasonPromptOpen(true);
  };

  const doReveal = async (v: Record<string, string>) => {
    setReasonPromptOpen(false);
    const reason = v.reason || 'admin_view';
    setRevealing(true);
    setRevealError(null);
    try {
      const res = await fetch(`${API}/admin/affiliates/${affiliateId}/pii?reason=${encodeURIComponent(reason)}`, { credentials: 'include' });
      const json = (await res.json()) as { success: boolean; data?: any; error?: string };
      if (!json.success) {
        setRevealError(json.error || 'Reveal failed');
        return;
      }
      setPii(json.data);
    } catch (e) {
      setRevealError(`Could not reach the server: ${String(e)}`);
    } finally {
      setRevealing(false);
    }
  };

  const Field = ({ label, value }: { label: string; value: any }) => (
    <div>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-sm text-gray-900">{value || '—'}</div>
    </div>
  );

  const PiiField = ({ label, lastFour, fullValue }: { label: string; lastFour: string | null; fullValue: string | null }) => (
    <div>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-sm text-gray-900 font-mono">
        {fullValue ? <span className="text-blue-700">{fullValue}</span> : (lastFour ? `••••${lastFour}` : '—')}
      </div>
    </div>
  );

  return (
    <div>
      {/* PII reveal control */}
      {(aff.tin_last_4 || aff.bank_account_last_4) && (
        <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-4 flex items-center justify-between">
          <div className="text-sm text-amber-900">
            {pii
              ? <span>⚠ PII is currently revealed below. Auto-hiding in 30 seconds.</span>
              : <span>TIN and bank account are masked. Click to reveal full values (audited).</span>}
          </div>
          <button
            onClick={reveal}
            disabled={revealing}
            className="px-3 py-1.5 bg-amber-600 text-white rounded text-sm hover:bg-amber-700 disabled:opacity-50"
          >
            {revealing ? 'Loading...' : pii ? 'Hide PII' : 'Reveal PII'}
          </button>
        </div>
      )}
      {revealError && <div className="text-sm text-red-600 mb-3">{revealError}</div>}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Field label="Legal Name" value={aff.legal_name} />
        <Field label="Business Name" value={aff.business_name} />
        <Field label="Phone" value={aff.phone} />
        <Field label="Address" value={[aff.address_line1, aff.address_line2].filter(Boolean).join(', ')} />
        <Field label="City / State" value={[aff.city, aff.state, aff.postal_code].filter(Boolean).join(', ')} />
        <Field label="Country" value={aff.country} />
        <Field label="TIN Type" value={aff.tin_type} />
        <PiiField label="TIN" lastFour={aff.tin_last_4} fullValue={pii?.tin || null} />
        <Field label="Federal Classification" value={aff.federal_classification} />
        <Field label="Bank Account Holder" value={aff.bank_account_holder_name} />
        <PiiField label="Bank Account #" lastFour={aff.bank_account_last_4} fullValue={pii?.bank_account || null} />
        <PiiField label="Bank Routing #" lastFour={null} fullValue={pii?.bank_routing || null} />
        <Field label="Bank Account Type" value={aff.bank_account_type} />
        <Field label="Linked Location ID" value={aff.linked_location_id} />
        <Field label="Linked Upline" value={aff.linked_upline_affiliate_id} />
        <Field label="Notes" value={aff.notes} />
      </div>

      <PromptModal
        isOpen={reasonPromptOpen}
        title="Reveal PII"
        message="Revealing the full TIN and bank details is logged to the audit trail. Provide a reason."
        submitLabel="Reveal"
        fields={[{ name: 'reason', label: 'Reason for access', type: 'text', required: true, defaultValue: 'admin_view' }]}
        onSubmit={doReveal}
        onCancel={() => setReasonPromptOpen(false)}
      />
    </div>
  );
}

function TiersTab({ affiliateId, affiliate }: { affiliateId: string; affiliate: any }) {
  const [data, setData] = useState<{ tiers: any[]; using_defaults: boolean } | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTiers = async () => {
    const res = await fetch(`${API}/admin/affiliates/${affiliateId}/tier-config`, { credentials: 'include' });
    const json = (await res.json()) as { success: boolean; data: { tiers: any[]; using_defaults: boolean } };
    if (json.success) {
      setData(json.data);
      setDraft(json.data.tiers || []);
    }
  };

  useEffect(() => {
    fetchTiers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    setError(null);
    const res = await fetch(`${API}/admin/affiliates/${affiliateId}/tier-config`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tiers: draft }),
    });
    const json = (await res.json()) as { success: boolean; error?: string };
    setSaving(false);
    if (!json.success) {
      setError(json.error || 'Save failed');
      return;
    }
    setEditing(false);
    fetchTiers();
  };

  if (!data) return <div className="text-sm text-gray-500">Loading tiers...</div>;

  if (!affiliate.tier_enabled) {
    return (
      <div className="text-sm text-gray-600">
        Tiers are <strong>not enabled</strong> for this affiliate. Flat rate {affiliate.default_commission_pct}% applies.
        To enable tiers, edit the affiliate and toggle "Tier enabled".
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <div>
          <span className="font-medium text-sm">Tier configuration</span>
          {data.using_defaults && <span className="ml-2 text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">Using system defaults</span>}
        </div>
        {!editing && (
          <button onClick={() => setEditing(true)} className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm">
            {data.using_defaults ? 'Customize' : 'Edit'}
          </button>
        )}
      </div>

      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="text-left p-2">Tier</th>
            <th className="text-left p-2">Min Referrals</th>
            <th className="text-left p-2">Commission %</th>
            <th className="text-left p-2">Override (same tier) %</th>
            {editing && <th></th>}
          </tr>
        </thead>
        <tbody>
          {draft.map((t, i) => (
            <tr key={i} className="border-b">
              <td className="p-2">
                {editing ? (
                  <input type="number" value={t.tier_level} onChange={(e) => updateDraft(setDraft, i, 'tier_level', parseInt(e.target.value))} className="w-16 border rounded px-2 py-1" />
                ) : t.tier_level}
              </td>
              <td className="p-2">
                {editing ? (
                  <input type="number" value={t.min_active_referrals} onChange={(e) => updateDraft(setDraft, i, 'min_active_referrals', parseInt(e.target.value))} className="w-20 border rounded px-2 py-1" />
                ) : t.min_active_referrals}
              </td>
              <td className="p-2">
                {editing ? (
                  <input type="number" step="0.01" value={t.commission_pct} onChange={(e) => updateDraft(setDraft, i, 'commission_pct', parseFloat(e.target.value))} className="w-20 border rounded px-2 py-1" />
                ) : `${t.commission_pct}%`}
              </td>
              <td className="p-2">
                {editing ? (
                  <input type="number" step="0.01" value={t.override_pct_when_upline_same_tier ?? ''} onChange={(e) => updateDraft(setDraft, i, 'override_pct_when_upline_same_tier', e.target.value === '' ? null : parseFloat(e.target.value))} className="w-20 border rounded px-2 py-1" placeholder="—" />
                ) : (t.override_pct_when_upline_same_tier !== null && t.override_pct_when_upline_same_tier !== undefined ? `${t.override_pct_when_upline_same_tier}%` : '—')}
              </td>
              {editing && (
                <td className="p-2">
                  <button onClick={() => setDraft(draft.filter((_, idx) => idx !== i))} className="text-red-600 text-xs hover:underline">Remove</button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {editing && (
        <div className="mt-3 flex gap-2">
          <button onClick={() => setDraft([...draft, { tier_level: draft.length + 1, min_active_referrals: 0, commission_pct: 15, override_pct_when_upline_same_tier: 2 }])} className="px-3 py-1.5 bg-gray-100 rounded-lg text-sm">+ Add Tier</button>
          <div className="flex-1" />
          <button onClick={() => { setEditing(false); setDraft(data.tiers); }} className="px-3 py-1.5 text-gray-700 text-sm">Cancel</button>
          <button onClick={save} disabled={saving} className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50">{saving ? 'Saving...' : 'Save Tiers'}</button>
        </div>
      )}

      {error && <div className="mt-2 text-sm text-red-600">{error}</div>}
    </div>
  );
}

function updateDraft(setDraft: any, i: number, field: string, value: any) {
  setDraft((prev: any[]) => prev.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));
}

function ReferralsTab({ affiliateId, referrals, onChange }: { affiliateId: string; referrals: any[]; onChange: () => void }) {
  const [locations, setLocations] = useState<any[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [locationId, setLocationId] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [removeReferralId, setRemoveReferralId] = useState<string | null>(null);
  const [voidReferralId, setVoidReferralId] = useState<string | null>(null);

  useEffect(() => {
    fetch(`${API}/admin/affiliates/locations-for-picker`, { credentials: 'include' })
      .then((r) => r.json() as Promise<{ success: boolean; data: any[] }>)
      .then((j) => j.success && setLocations(j.data));
  }, []);

  const addReferral = async () => {
    setError(null);
    if (!locationId || !effectiveDate) {
      setError('Location and effective date required');
      return;
    }
    try {
      const res = await fetch(`${API}/admin/affiliates/${affiliateId}/referrals`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ location_id: locationId, effective_date: effectiveDate, notes }),
      });
      const json = (await res.json()) as { success: boolean; error?: string };
      if (!json.success) {
        setError(json.error || 'Failed to add referral');
        return;
      }
    } catch (e) {
      setError(`Could not reach the server: ${String(e)}`);
      return;
    }
    setShowAdd(false);
    setLocationId('');
    setNotes('');
    onChange();
  };

  const submitRemoveReferral = async (v: Record<string, string>) => {
    const referralId = removeReferralId;
    setRemoveReferralId(null);
    if (!referralId) return;
    try {
      const res = await fetch(`${API}/admin/affiliates/referrals/${referralId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ effective_end_date: v.end_date, reason: v.reason }),
      });
      const json = (await res.json()) as { success: boolean; error?: string };
      if (!json.success) setError(json.error || 'Failed to remove referral');
    } catch (e) {
      setError(`Could not reach the server: ${String(e)}`);
    }
    onChange();
  };

  const submitVoidReferral = async () => {
    const referralId = voidReferralId;
    setVoidReferralId(null);
    if (!referralId) return;
    try {
      const res = await fetch(`${API}/admin/affiliates/referrals/${referralId}/void`, {
        method: 'POST',
        credentials: 'include',
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const json = (await res.json()) as { success: boolean; error?: string; data?: any };
      if (!json.success) {
        setError(json.error || 'Failed to void referral');
      } else if (json.data?.adjustments?.length > 0) {
        // Surface what happened so the admin sees the financial side effect.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const total = json.data.adjustments.reduce((s: number, a: any) => s + Number(a.amount || 0), 0);
        setError(`Referral removed. ${json.data.adjustments.length} retroactive_debit adjustment(s) posted totaling $${total.toFixed(2)} (commission previously earned on this referral has been reversed).`);
      }
    } catch (e) {
      setError(`Could not reach the server: ${String(e)}`);
    }
    onChange();
  };

  const locById = new Map(locations.map((l) => [l.location_id, l.billing_display_name]));

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <span className="font-medium text-sm">Referrals ({referrals.length})</span>
        <button onClick={() => setShowAdd(!showAdd)} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm">
          {showAdd ? 'Cancel' : '+ Add Referral'}
        </button>
      </div>

      {showAdd && (
        <div className="bg-gray-50 p-4 rounded-lg mb-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-600">Location</label>
              <select value={locationId} onChange={(e) => setLocationId(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm">
                <option value="">Select a location...</option>
                {locations.map((l) => (
                  <option key={l.location_id} value={l.location_id}>
                    {l.billing_display_name || l.location_id}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-600">Effective Date</label>
              <input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-600">Notes</label>
              <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" />
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <button onClick={addReferral} className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm">Save</button>
            {error && <span className="text-sm text-red-600 self-center">{error}</span>}
          </div>
        </div>
      )}

      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="text-left p-2">Location</th>
            <th className="text-left p-2">Start Date</th>
            <th className="text-left p-2">End Date</th>
            <th className="text-left p-2">Method</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {referrals.map((r) => (
            <tr key={r.id} className="border-b">
              <td className="p-2">
                <div className="font-medium">{locById.get(r.location_id) || r.location_id}</div>
                <div className="text-xs text-gray-500 font-mono">{r.location_id}</div>
              </td>
              <td className="p-2">{r.effective_start_date}</td>
              <td className="p-2">{r.effective_end_date || <span className="text-green-600">Active</span>}</td>
              <td className="p-2 text-xs">{r.created_by_method}</td>
              <td className="p-2">
                <div className="flex gap-3 justify-end">
                  {!r.effective_end_date && (
                    <button onClick={() => setRemoveReferralId(r.id)} className="text-red-600 text-xs hover:underline">End</button>
                  )}
                  <button
                    onClick={() => setVoidReferralId(r.id)}
                    title="Hard-delete this referral (for data entry mistakes). Posts a retroactive_debit adjustment if commission was already calculated."
                    className="text-rose-700 text-xs hover:underline"
                  >
                    Remove (mistake)
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <PromptModal
        isOpen={!!removeReferralId}
        title="End Referral"
        message="Set the date this referral relationship ends. Commission stops accruing for plan payments after this date."
        danger
        submitLabel="End Referral"
        fields={[
          { name: 'end_date', label: 'Effective end date', type: 'date', required: true, defaultValue: new Date().toISOString().slice(0, 10) },
          { name: 'reason', label: 'Reason', type: 'textarea', required: true },
        ]}
        onSubmit={submitRemoveReferral}
        onCancel={() => setRemoveReferralId(null)}
      />

      <ConfirmModal
        isOpen={!!voidReferralId}
        title="Remove referral (mistake)?"
        message={
          'This permanently DELETES the referral row from the database. Use this only when the referral was attributed to the wrong affiliate or otherwise added in error.\n\n' +
          'If commission has already been calculated against this referral (primary OR override on an upline), a retroactive_debit adjustment will be posted automatically on each affected affiliate\'s most-recent open period so their running balance reflects the reversal. Historical periods and line items stay intact for audit.\n\n' +
          'This action cannot be undone.'
        }
        danger
        confirmLabel="Remove"
        onConfirm={submitVoidReferral}
        onCancel={() => setVoidReferralId(null)}
      />
    </div>
  );
}

function PeriodsTab({ periods }: { periods: any[] }) {
  if (periods.length === 0) return <div className="text-sm text-gray-500">No periods yet.</div>;
  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-50">
        <tr>
          <th className="text-left p-2">Period</th>
          <th className="text-left p-2">Status</th>
          <th className="text-right p-2">Gross Rev</th>
          <th className="text-right p-2">Primary</th>
          <th className="text-right p-2">Override</th>
          <th className="text-right p-2">Adjustments</th>
          <th className="text-right p-2">Total Due</th>
          <th className="text-right p-2">Paid</th>
        </tr>
      </thead>
      <tbody>
        {periods.map((p) => (
          <tr key={p.id} className="border-b">
            <td className="p-2">{p.period_start}</td>
            <td className="p-2"><span className={`px-2 py-0.5 rounded text-xs ${statusBadge(p.status)}`}>{p.status}</span></td>
            <td className="p-2 text-right font-mono">${Number(p.gross_referral_revenue || 0).toFixed(2)}</td>
            <td className="p-2 text-right font-mono">${Number(p.total_primary_commission || 0).toFixed(2)}</td>
            <td className="p-2 text-right font-mono">${Number(p.total_override_commission || 0).toFixed(2)}</td>
            <td className="p-2 text-right font-mono">${Number(p.total_adjustments || 0).toFixed(2)}</td>
            <td className="p-2 text-right font-mono font-bold">${Number(p.total_due || 0).toFixed(2)}</td>
            <td className="p-2 text-right font-mono">${Number(p.total_paid || 0).toFixed(2)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PaymentsTab({ payments }: { payments: any[] }) {
  if (payments.length === 0) return <div className="text-sm text-gray-500">No payments recorded.</div>;
  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-50">
        <tr>
          <th className="text-left p-2">Paid At</th>
          <th className="text-left p-2">Method</th>
          <th className="text-right p-2">Amount</th>
          <th className="text-left p-2">Reference</th>
          <th className="text-left p-2">Notes</th>
        </tr>
      </thead>
      <tbody>
        {payments.map((p) => (
          <tr key={p.id} className="border-b">
            <td className="p-2">{new Date(p.paid_at).toLocaleDateString()}</td>
            <td className="p-2 text-xs">{p.method}</td>
            <td className="p-2 text-right font-mono">${Number(p.amount).toFixed(2)}</td>
            <td className="p-2 text-xs font-mono">{p.reference_number || '—'}</td>
            <td className="p-2 text-xs text-gray-600">{p.notes || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DocumentsTab({ documents }: { documents: any[] }) {
  if (documents.length === 0) return <div className="text-sm text-gray-500">No documents yet. W-9 PDFs will appear here once Phase 3 (affiliate portal) is live.</div>;
  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-50">
        <tr>
          <th className="text-left p-2">Type</th>
          <th className="text-left p-2">Version</th>
          <th className="text-left p-2">Generated</th>
          <th className="text-left p-2">Status</th>
          <th className="text-left p-2">R2 Key</th>
        </tr>
      </thead>
      <tbody>
        {documents.map((d) => (
          <tr key={d.id} className="border-b">
            <td className="p-2 uppercase">{d.document_type}</td>
            <td className="p-2">v{d.version}</td>
            <td className="p-2">{new Date(d.generated_at).toLocaleDateString()}</td>
            <td className="p-2">{d.superseded_at ? <span className="text-gray-400">superseded</span> : <span className="text-green-600">current</span>}</td>
            <td className="p-2 text-xs font-mono text-gray-500">{d.r2_key}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ============================================================================
// Affiliate Create/Edit Form
// ============================================================================

interface AffiliateFormProps {
  affiliateId: string | null;
  onCancel: () => void;
  onSaved: (id: string) => void;
}

// Reusable Field component defined OUTSIDE the form to prevent React re-mounting
// (which was causing the focus-loss bug — every keystroke recreated the inputs).
function FormField({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs text-gray-600 block">{label}</label>
      {children}
      {hint && <div className="text-xs text-gray-500 mt-1">{hint}</div>}
    </div>
  );
}

function TextInput({ value, onChange, type = 'text', placeholder = '' }: { value: string; onChange: (v: string) => void; type?: string; placeholder?: string }) {
  return (
    <input
      type={type}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full border rounded px-2 py-1.5 text-sm"
    />
  );
}

function NumberInput({ value, onChange, step = '1' }: { value: number | string; onChange: (v: number) => void; step?: string }) {
  return (
    <input
      type="number"
      step={step}
      value={value ?? ''}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      className="w-full border rounded px-2 py-1.5 text-sm"
    />
  );
}

function AffiliateForm({ affiliateId, onCancel, onSaved }: AffiliateFormProps) {
  const isNew = !affiliateId;
  const [form, setForm] = useState<any>({
    display_name: '',
    legal_name: '',
    business_name: '',
    email: '',
    phone: '',
    address_line1: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
    tin_type: '',
    full_tin: '',                  // plaintext, encrypted server-side
    federal_classification: '',
    bank_account_holder_name: '',
    full_routing: '',              // plaintext, encrypted server-side
    full_account: '',              // plaintext, encrypted server-side
    bank_account_type: '',
    payment_method_preference: 'check',
    linked_location_id: '',
    linked_upline_affiliate_id: '',
    tier_enabled: false,
    second_level_enabled: false,
    default_commission_pct: 15,
    effective_start_date: new Date().toISOString().slice(0, 10),
    notes: '',
  });
  const [existing, setExisting] = useState<any>(null);  // for edit: stores last_4 to display
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locations, setLocations] = useState<any[]>([]);
  const [upliners, setUpliners] = useState<any[]>([]);

  useEffect(() => {
    fetch(`${API}/admin/affiliates/locations-for-picker`, { credentials: 'include' })
      .then((r) => r.json() as Promise<{ success: boolean; data: any[] }>)
      .then((j) => j.success && setLocations(j.data));

    const uplinerUrl = affiliateId
      ? `${API}/admin/affiliates/picker?exclude=${affiliateId}`
      : `${API}/admin/affiliates/picker`;
    fetch(uplinerUrl, { credentials: 'include' })
      .then((r) => r.json() as Promise<{ success: boolean; data: any[] }>)
      .then((j) => j.success && setUpliners(j.data));

    if (affiliateId) {
      fetch(`${API}/admin/affiliates/${affiliateId}`, { credentials: 'include' })
        .then((r) => r.json() as Promise<{ success: boolean; data: any }>)
        .then((j) => {
          if (!j.success) return;
          const a = j.data.affiliate;
          setExisting(a);
          setForm((f: any) => ({
            ...f,
            ...a,
            full_tin: '',           // never pre-fill plaintext PII
            full_routing: '',
            full_account: '',
            linked_location_id: a.linked_location_id || '',
            linked_upline_affiliate_id: a.linked_upline_affiliate_id || '',
          }));
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [affiliateId]);

  const setField = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  // When user picks a Linked Location, auto-populate display_name from billing_display_name
  // (only if display_name is empty — don't clobber user typing)
  const onLocationSelect = (locationId: string) => {
    setField('linked_location_id', locationId);
    if (locationId && !form.display_name) {
      const loc = locations.find((l) => l.location_id === locationId);
      if (loc?.billing_display_name) {
        setForm((f: any) => ({ ...f, linked_location_id: locationId, display_name: loc.billing_display_name }));
      }
    }
  };

  const save = async () => {
    setLoading(true);
    setError(null);
    const url = affiliateId ? `${API}/admin/affiliates/${affiliateId}` : `${API}/admin/affiliates`;
    const method = affiliateId ? 'PUT' : 'POST';
    const payload = {
      ...form,
      linked_location_id: form.linked_location_id || null,
      linked_upline_affiliate_id: form.linked_upline_affiliate_id || null,
      // Strip empty PII strings so backend treats them as "no change"
      full_tin: form.full_tin?.trim() || undefined,
      full_routing: form.full_routing?.trim() || undefined,
      full_account: form.full_account?.trim() || undefined,
    };
    try {
      const res = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      // Read as text first so a non-JSON error response (e.g. a raw 500) doesn't throw
      const raw = await res.text();
      let json: { success: boolean; data?: any; error?: string; partial?: boolean };
      try {
        json = JSON.parse(raw);
      } catch {
        setError(`Server returned an unexpected response (HTTP ${res.status}). ${raw.slice(0, 200)}`);
        return;
      }
      if (!json.success) {
        // partial=true means the affiliate WAS created but PII failed — still navigate to it
        if (json.partial && json.data?.id) {
          setError(json.error || 'Saved with warnings');
          onSaved(json.data.id);
          return;
        }
        setError(json.error || 'Save failed');
        return;
      }
      onSaved(json.data?.id || affiliateId);
    } catch (e) {
      setError(`Could not reach the server: ${String(e)}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <button onClick={onCancel} className="flex items-center gap-2 text-blue-600 hover:underline mb-4 text-sm">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">{affiliateId ? 'Edit Affiliate' : 'New Affiliate'}</h2>

        <p className="text-sm text-gray-600 mb-4">
          Only <strong>Display Name</strong> and <strong>Effective Start Date</strong> are required to create the record (status: <em>onboarding</em>).
          Legal name + TIN are required before transitioning to <em>enrolled</em> status (i.e., before payments will occur).
        </p>

        <AlertModal isOpen={!!error} title="Could not save affiliate" message={error || ''} onClose={() => setError(null)} />

        {/* ============ Section 1: Basics + Linked Location (autofill) ============ */}
        <fieldset className="border rounded-lg p-4 mb-4">
          <legend className="text-sm font-semibold text-gray-700 px-2">Basics</legend>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField label="Linked Location (if also a customer)" hint="Selecting a location auto-fills the Display Name with the location's billing display name.">
              <select value={form.linked_location_id ?? ''} onChange={(e) => onLocationSelect(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm">
                <option value="">— None / Not a customer —</option>
                {locations.map((l) => (
                  <option key={l.location_id} value={l.location_id}>{l.billing_display_name || l.location_id}</option>
                ))}
              </select>
            </FormField>
            <FormField label="Display Name *">
              <TextInput value={form.display_name} onChange={(v) => setField('display_name', v)} />
            </FormField>
            <FormField label="Effective Start Date *" hint="Can be backdated to historical join date.">
              <TextInput type="date" value={form.effective_start_date} onChange={(v) => setField('effective_start_date', v)} />
            </FormField>
          </div>
        </fieldset>

        {/* ============ Section 2: Contact ============ */}
        <fieldset className="border rounded-lg p-4 mb-4">
          <legend className="text-sm font-semibold text-gray-700 px-2">Contact</legend>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField label="Email" hint="Required for affiliate portal login (Phase 3).">
              <TextInput type="email" value={form.email} onChange={(v) => setField('email', v)} />
            </FormField>
            <FormField label="Phone">
              <TextInput value={form.phone} onChange={(v) => setField('phone', v)} />
            </FormField>
            <div></div>
          </div>
        </fieldset>

        {/* ============ Section 3: W-9 / Tax ============ */}
        <fieldset className="border rounded-lg p-4 mb-4">
          <legend className="text-sm font-semibold text-gray-700 px-2">W-9 / Tax</legend>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <FormField label="Legal Name (W-9)" hint="Required before enrollment.">
              <TextInput value={form.legal_name} onChange={(v) => setField('legal_name', v)} />
            </FormField>
            <FormField label="Business Name (W-9 line 2, optional)">
              <TextInput value={form.business_name} onChange={(v) => setField('business_name', v)} />
            </FormField>
            <FormField label="Federal Classification (W-9 box 3)">
              <TextInput value={form.federal_classification} onChange={(v) => setField('federal_classification', v)} placeholder="e.g., Individual / S-Corp / LLC" />
            </FormField>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <FormField label="Address">
              <TextInput value={form.address_line1} onChange={(v) => setField('address_line1', v)} />
            </FormField>
            <FormField label="City">
              <TextInput value={form.city} onChange={(v) => setField('city', v)} />
            </FormField>
            <div className="grid grid-cols-2 gap-2">
              <FormField label="State">
                <TextInput value={form.state} onChange={(v) => setField('state', v)} />
              </FormField>
              <FormField label="ZIP">
                <TextInput value={form.postal_code} onChange={(v) => setField('postal_code', v)} />
              </FormField>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField label="TIN Type" hint="Required before enrollment.">
              <select value={form.tin_type ?? ''} onChange={(e) => setField('tin_type', e.target.value || null)} className="w-full border rounded px-2 py-1.5 text-sm">
                <option value="">—</option>
                <option value="ssn">SSN</option>
                <option value="ein">EIN</option>
              </select>
            </FormField>
            <FormField
              label={existing?.tin_last_4 ? `Full TIN (currently set: ••••${existing.tin_last_4})` : 'Full TIN'}
              hint={existing?.tin_last_4 ? 'Leave blank to keep existing. Type a new TIN to replace.' : 'Stored encrypted server-side. Display as last-4 going forward.'}
            >
              <TextInput type="password" value={form.full_tin} onChange={(v) => setField('full_tin', v)} placeholder={existing?.tin_last_4 ? '— unchanged —' : '123-45-6789 or 123456789'} />
            </FormField>
            <div></div>
          </div>
        </fieldset>

        {/* ============ Section 4: Payment ============ */}
        <fieldset className="border rounded-lg p-4 mb-4">
          <legend className="text-sm font-semibold text-gray-700 px-2">Payment</legend>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <FormField label="Payment Method" hint="Wallet Credit only works if a Linked Location is set.">
              <select value={form.payment_method_preference} onChange={(e) => setField('payment_method_preference', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm">
                <option value="check">Check</option>
                <option value="ach_manual">ACH (Manual)</option>
                <option value="wallet_credit">Wallet Credit</option>
                <option value="stripe_connect">Stripe Connect (future)</option>
              </select>
            </FormField>
            <div></div>
            <div></div>
          </div>
          {(form.payment_method_preference === 'ach_manual' || form.payment_method_preference === 'stripe_connect') && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Bank Account Holder">
                <TextInput value={form.bank_account_holder_name} onChange={(v) => setField('bank_account_holder_name', v)} />
              </FormField>
              <FormField label="Bank Account Type">
                <select value={form.bank_account_type ?? ''} onChange={(e) => setField('bank_account_type', e.target.value || null)} className="w-full border rounded px-2 py-1.5 text-sm">
                  <option value="">—</option>
                  <option value="checking">Checking</option>
                  <option value="savings">Savings</option>
                </select>
              </FormField>
              <FormField
                label={existing?.bank_account_last_4 ? `Routing # (account currently ends ••••${existing.bank_account_last_4})` : 'Routing #'}
                hint="Stored encrypted. Leave blank to keep existing."
              >
                <TextInput type="password" value={form.full_routing} onChange={(v) => setField('full_routing', v)} placeholder={existing?.bank_account_last_4 ? '— unchanged —' : '9 digits'} />
              </FormField>
              <FormField
                label="Account #"
                hint="Stored encrypted. Leave blank to keep existing."
              >
                <TextInput type="password" value={form.full_account} onChange={(v) => setField('full_account', v)} placeholder={existing?.bank_account_last_4 ? '— unchanged —' : '7-12 digits'} />
              </FormField>
            </div>
          )}
        </fieldset>

        {/* ============ Section 5: Affiliate Program Settings ============ */}
        <fieldset className="border rounded-lg p-4 mb-4">
          <legend className="text-sm font-semibold text-gray-700 px-2">Affiliate Program Settings</legend>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <FormField label="Default Commission %" hint="Used when Tier enabled is OFF.">
              <NumberInput value={form.default_commission_pct} onChange={(v) => setField('default_commission_pct', v)} step="0.01" />
            </FormField>
            <FormField label="Brought in by (upline affiliate, optional)" hint="If THIS affiliate was referred to the program by another affiliate, select them here. They will earn override commissions on this affiliate's referrals.">
              <select value={form.linked_upline_affiliate_id ?? ''} onChange={(e) => setField('linked_upline_affiliate_id', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm">
                <option value="">— No upline —</option>
                {upliners.map((u) => (
                  <option key={u.id} value={u.id}>{u.display_name} ({u.referral_code})</option>
                ))}
              </select>
            </FormField>
            <div></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="flex items-start gap-2">
              <input type="checkbox" checked={form.tier_enabled} onChange={(e) => setField('tier_enabled', e.target.checked)} className="mt-1" />
              <span>
                <span className="text-sm font-medium">Tier enabled</span>
                <div className="text-xs text-gray-500">Commission rate scales with active referral count instead of using flat default. {isNew ? 'After creating the affiliate, configure tier thresholds on the Tiers tab (or system defaults will be used: 15% / 20% / 25% at 0 / 5 / 20 active referrals).' : 'Configure thresholds on the Tiers tab.'}</div>
              </span>
            </label>
            <label className="flex items-start gap-2">
              <input type="checkbox" checked={form.second_level_enabled} onChange={(e) => setField('second_level_enabled', e.target.checked)} className="mt-1" />
              <span>
                <span className="text-sm font-medium">Second-level override enabled</span>
                <div className="text-xs text-gray-500">Allows THIS affiliate to earn override commissions when they refer another affiliate (the upline they're listed as on someone else's record).</div>
              </span>
            </label>
          </div>
        </fieldset>

        {/* ============ Notes ============ */}
        <fieldset className="border rounded-lg p-4 mb-4">
          <legend className="text-sm font-semibold text-gray-700 px-2">Internal Notes</legend>
          <textarea value={form.notes ?? ''} onChange={(e) => setField('notes', e.target.value)} rows={3} className="w-full border rounded px-2 py-1.5 text-sm" placeholder="Admin-only notes; not visible to the affiliate." />
        </fieldset>

        <div className="mt-6 flex gap-2 justify-end">
          <button onClick={onCancel} className="px-4 py-2 text-gray-700">Cancel</button>
          <button onClick={save} disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50">
            {loading ? 'Saving...' : affiliateId ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}
